import React, { useState } from "react";
import { Action, ActionPosition, BaseActionProps } from "../types";
import { cn } from "@/lib/utils";

const positionClasses: Record<ActionPosition, string> = {
  "top-left": "top-2 left-2",
  "top-right": "top-2 right-2",
  "bottom-left": "bottom-2 left-2",
  "bottom-right": "bottom-2 right-2",
};

const getActionPositionClass = (position: ActionPosition): string =>
  positionClasses[position];

interface MediaActionsProps {
  actions: Action[];
  isMediaLoaded: boolean;
  isMediaHovered: boolean;
  isMediaPlaying: boolean;
  isReady?: boolean;
}

interface ActionComponentProps {
  action: Action;
  isMediaHovered: boolean;
  isMediaPlaying: boolean;
  isReady?: boolean;
}

const ActionComponent = ({
  action,
  isMediaHovered,
  isMediaPlaying,
  isReady,
}: ActionComponentProps): JSX.Element => {
  const [forcedVisibility, setForcedVisibility] = useState(false);

  if (
    React.isValidElement<Partial<BaseActionProps> & Record<string, unknown>>(
      action.component,
    )
  ) {
    const isHidden =
      !isReady && !(action.visibility === "always-hover" && isMediaHovered);
    const isVisible = isReady
      ? forcedVisibility ||
        action.visibility === "always" ||
        (action.visibility === "hover" && isMediaHovered) ||
        (action.visibility === "always-hover" && isMediaHovered) ||
        (action.visibility === "playing" && isMediaPlaying)
      : action.visibility === "always-hover" && isMediaHovered;

    return (
      <div
        className={cn(
          "transition-opacity duration-300 backdrop-blur-[3px] rounded-[8px]",
          isVisible ? "opacity-100" : "opacity-0 pointer-events-none",
          isHidden && "hidden",
          action.className,
        )}
      >
        {React.cloneElement(action.component, {
          ...action.component.props,
          forcedVisibility: setForcedVisibility,
        })}
      </div>
    );
  }
  return <></>;
};

interface ActionGroupProps {
  position: ActionPosition;
  actions: Action[];
  isMediaHovered: boolean;
  isMediaPlaying: boolean;
  isReady?: boolean;
}

const ActionGroup: React.FC<ActionGroupProps> = ({
  position,
  actions,
  isMediaHovered,
  isMediaPlaying,
  isReady,
}) => {
  if (actions.length === 0) return null;

  return (
    <div
      className={cn(
        "absolute pointer-events-auto",
        getActionPositionClass(position),
        "flex flex-row gap-2",
      )}
    >
      {actions.map((action: Action, index: number) => (
        <div
          key={`${action.key || action.position}-${index}`}
          onClick={(e) => e.stopPropagation()}
        >
          <ActionComponent
            action={action}
            isMediaHovered={isMediaHovered}
            isMediaPlaying={isMediaPlaying}
            isReady={isReady}
          />
        </div>
      ))}
    </div>
  );
};

export const MediaActions: React.FC<MediaActionsProps> = ({
  actions,
  isMediaLoaded,
  isMediaHovered,
  isMediaPlaying,
  isReady,
}) => {
  if (!isMediaLoaded || !actions?.length) return null;

  const adjustedActions = actions.map((action) => {
    return {
      ...action,
      position:
        action.position === "bottom-left" && isMediaPlaying
          ? "top-left"
          : action.position,
    };
  });
  // Group actions by position
  const groupedActions = adjustedActions.reduce<Record<ActionPosition, Action[]>>(
    (acc, action) => {
      const position = action.position;
      if (!acc[position]) {
        acc[position] = [];
      }
      acc[position].push(action);
      return acc;
    },
    {
      "top-left": [],
      "top-right": [],
      "bottom-left": [],
      "bottom-right": [],
    },
  );

  return (
    <div className="absolute pointer-events-none inset-0">
      {Object.entries(groupedActions).map(([position, positionActions]) => (
        <ActionGroup
          key={position}
          position={position as ActionPosition}
          actions={positionActions}
          isMediaHovered={isMediaHovered}
          isMediaPlaying={isMediaPlaying}
          isReady={isReady}
        />
      ))}
    </div>
  );
};

export default MediaActions;
